import features from 'config/featureFlags';
import checkoutRoutes from 'pages/checkout/routes';
import orderImportRoutes from 'pages/OrderImport/routes';
import { pageGenerator } from './helpers';
import type { GeneratePageParams, PageSchema } from './interfaces';

const ordersData: Record<string, Omit<GeneratePageParams, 'pathname'>> = {
	details: {
		pageDirPath: '/pos/Orders/Details',
		title: 'Detalhes do pedido - Meus pedidos - Minhas farmácias',
		headerType: 'home',

		type: 'PRIVATE',
		needContext: 'POS',
		allowedRestrictions: ['SIMULATED', 'PARTNER'],
	},
	list: {
		pageDirPath: '/pos/Orders/List',
		title: 'Meus pedidos - Minhas farmácias',
		headerType: 'home',

		type: 'PRIVATE',
		needContext: 'POS',
		allowedRestrictions: ['SIMULATED', 'PARTNER'],
	},
};

const loginData: Omit<GeneratePageParams, 'pathname'> = {
	pageDirPath: '/auth/Login/variants/Default',
	title: 'Login',
	headerType: undefined,

	type: 'NOT_LOGGED',
	needContext: null,
};

const orderOptimizationData: Omit<GeneratePageParams, 'pathname'> = {
	pageDirPath: '/checkout/OrderOptimization',
	title: 'Otimização de pedido',

	type: 'PRIVATE',
	needContext: null,
};

const resetPassword: Omit<GeneratePageParams, 'pathname' | 'type'> = {
	pageDirPath: '/auth/ResetPassword',
	title: 'Recuperar senha',
	headerType: 'logged_and_not_logged',

	needContext: null,
};

export const pages: PageSchema = {
	generic: [
		{
			pageDirPath: '/WorldCup',
			title: 'Ranking do Bolão da Copa',
			pathname: '/bolao-da-copa',
			headerType: 'home',

			type: 'PRIVATE',
			needContext: 'POS',

			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/Faq',
			title: 'Ficou com alguma dúvida?',
			pathname: '/perguntas-frequentes',
			headerType: 'logged_and_not_logged',

			type: 'PUBLIC',
			needContext: null,
		},
		{
			pageDirPath: '/Fidelity',
			title: 'Fidelidade',
			pathname: '/fidelidade',
			headerType: undefined,
			showFooter: false,

			type: 'PUBLIC',
			needContext: null,
		},
		{
			pageDirPath: '/Fidelity/ValidateFidelity',
			title: 'Validar Fidelidade',
			pathname: '/validar-fidelidade',
			headerType: undefined,
			showFooter: false,

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED'],
		},
		{
			pageDirPath: '/LandingPage',
			title: 'Landing Page',
			pathname: '/lp/:campaign',
			headerType: undefined,

			type: 'PUBLIC',
			needContext: null,
		},
	],
	pos: [
		{
			pageDirPath: '/pos/Distributors',
			title: 'Distribuidores - Minhas farmácias',
			pathname: '/minhas-farmacias/distribuidores',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/pos/FirstAccess',
			title: 'Primeiro Acesso',
			pathname: '/primeiro-acesso',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			underRestriction: 'NO_LINKS',
		},
		{
			pageDirPath: '/pos/Information',
			title: 'Dados da farmácia - Minhas farmácias',
			pathname: '/minhas-farmacias/dados',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/pos/BankData',
			title: 'Cadastrar conta bancária',
			pathname: '/minhas-farmacias/dados/cadastrar-conta-bancaria',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['PARTNER'],
		},
		{
			pageDirPath: '/pos/List',
			title: 'Minhas farmácias',
			pathname: '/minhas-farmacias',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['NO_LINKS'],
		},
		{
			pageDirPath: '/pos/ManageAccess',
			title: 'Acessos - Minhas farmácias',
			pathname: '/minhas-farmacias/acessos',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pathname: '/old/pedidos',
			...ordersData.list,
			pageDirPath: '/pos/Orders/List_old',
		},
		{ pathname: '/pedidos', ...ordersData.list },
		{ pathname: '/minhas-farmacias/pedidos', ...ordersData.list },
		{ pathname: '/pedidos/:order', ...ordersData.details },
		{ pathname: '/minhas-farmacias/pedidos/:order', ...ordersData.details },
		{
			pageDirPath: '/pos/RequestLink',
			title: 'Solicitar acesso - Minhas farmácias',
			pathname: '/minhas-farmacias/vincular-farmacia',
			enabled: features.POS_REGISTER,
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['NO_LINKS', 'SIMULATED'],
		},
		{
			pageDirPath: '/pos/Simulate',
			title: 'Simular farmácia',
			pathname: '/simular-farmacia',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			underRestriction: 'SIMULATED',
		},
	],
	user: [
		{
			pageDirPath: '/user/Information',
			title: 'Meus dados - Meu perfil',
			subtitle: 'Aqui você pode ver e alterar seus dados, seu e-mail e sua senha.',
			pathname: '/meu-perfil/dados',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['SIMULATED', 'QUOTATION', 'NO_LINKS', 'PARTNER'],
		},
		{
			pageDirPath: '/user/UpdateEmail',
			title: 'Atualizar email - Meus dados - Meu perfil',
			subtitle: 'Preencha os dados abaixo para realizar a alteração de e-mail',
			pathname: '/meu-perfil/dados/alterar-email',
			featureFlag: 'CUSTOMER_DATA_EMAIL_CHANGE',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['SIMULATED', 'NO_LINKS'],
		},
		{
			pageDirPath: '/user/UpdateEmailConfirmation',
			title: 'Confirmação - Atualizar email - Meus dados - Meu perfil',
			subtitle: 'Insira o código de 6 dígitos que foi enviado via e-mail.',
			pathname: '/meu-perfil/dados/alterar-email/confirmacao',
			featureFlag: 'CUSTOMER_DATA_EMAIL_CHANGE',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['SIMULATED', 'NO_LINKS'],
		},
	],
	auth: [
		{
			pageDirPath: '/auth/Verification',
			title: 'Verifique a sua identidade',
			pathname: '/verificacao-de-identidade',
			headerType: 'not_logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['2FA_EXPIRED', 'QUOTATION'],
		},
		{
			pageDirPath: '/auth/SignUp',
			title: 'Cadastro',
			pathname: '/cadastro',
			headerType: 'not_logged',

			type: 'NOT_LOGGED',
			needContext: null,
		},
		{
			pathname: '/redefinir-senha',
			type: 'NOT_LOGGED',
			showHelpButton: true,
			...resetPassword,
		},
		{
			pathname: '/meu-perfil/dados/alterar-senha',
			type: 'PRIVATE',
			allowedRestrictions: ['SIMULATED', 'NO_LINKS'],
			...resetPassword,
		},

		{ pathname: '/rv', ...loginData },
		{ pathname: '/login', ...loginData },
		{
			pathname: '/acesso-distribuidor',
			title: 'Acesso distribuidor',
			pageDirPath: '/auth/Login/variants/PartnerAccess',
			headerType: undefined,

			type: 'NOT_LOGGED',
			needContext: null,
		},
	],
	policy: [
		{
			pageDirPath: '/policy/Reduced',
			title: 'Normas de Segurança e Privacidade',
			pathname: '/politicas-de-privacidade',
			headerType: 'logged_and_not_logged',

			type: 'PUBLIC',
			needContext: null,
		},
		{
			pageDirPath: '/policy/Complete',
			title: 'Normas de Segurança e Privacidade',
			pathname: '/politicas-de-privacidade/completo',
			headerType: 'logged_and_not_logged',

			type: 'PUBLIC',
			needContext: null,
		},
	],
	checkout: [
		{
			pathname: '/otimizacao-de-pedido',
			...orderOptimizationData,
			headerType: 'home',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		...checkoutRoutes,
	],
	products: [
		{
			pageDirPath: '/products/ProductDetails',
			title: 'Detalhes do Produto',
			pathname: '/detalhes-do-produto/:productId',
			headerType: 'home',

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/ProductsSearch',
			title: 'Busca de produtos',
			pathname: '/busca/:search',
			headerType: 'home',

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/IncentiveCampaign',
			title: 'Bateu, Ganhou',
			pathname: '/bateu-ganhou',
			headerType: 'home',
			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/IncentiveCampaign/Challenge',
			title: 'Bateu, Ganhou',
			pathname: '/bateu-ganhou/:challengeId',
			headerType: 'home',
			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/IncentiveCampaign/Extract',
			title: 'Extrato das campanhas',
			pathname: '/bateu-ganhou/extrato',
			headerType: 'home',
			needContext: 'POS',
			type: 'PRIVATE',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/IncentiveCampaign/Extract/View',
			title: 'Visualizar Extrato',
			pathname: '/bateu-ganhou/visualizar-extrato',
			headerType: 'hide',
			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
			showFooter: false,
		},
		{
			pageDirPath: '/products/IncentiveCampaign/Rules',
			title: 'Entenda as regras',
			pathname: '/bateu-ganhou/regras',
			headerType: 'home',
			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/Departments',
			title: 'Visualizar categoria',
			pathname: '/departamentos/:departmentId/categorias/:categoryId',
			headerType: 'home',
			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/ProductsBrand',
			title: 'Visualizar Marca',
			pathname: '/nossas-marcas/:brandId',
			headerType: 'home',

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/PromotionsWithTheProduct',
			title: 'Promoções com o produto',
			pathname: '/produto/:productId/promocoes',
			headerType: 'home',

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/Promotions',
			title: 'Promoções',
			pathname: '/promocoes',
			headerType: 'home',

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/PromotionDetails',
			title: 'Detalhes da promoção',
			pathname: '/promocoes/:hyperaCode',
			headerType: 'home',
			stickyTopRow: true,

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/Releases',
			title: 'Lançamentos',
			pathname: '/lancamentos',
			headerType: 'home',

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/Home',
			title: 'Bem-vindo ao Hympar',
			pathname: '/',
			headerType: 'home',

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/Suggestions',
			title: 'Sugestões de pedidos',
			pathname: '/sugestoes-de-pedidos',
			headerType: 'home',

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
		{
			pageDirPath: '/products/Brands',
			title: 'Nossas Marcas',
			pathname: '/nossas-marcas',
			headerType: 'home',

			type: 'PRIVATE',
			needContext: 'POS',
			allowedRestrictions: ['SIMULATED', 'PARTNER'],
		},
	],
	reports: [
		{
			pageDirPath: '/reports/Download',
			title: 'Busca de produtos',
			pathname: '/relatorios/:reportId/download',
			headerType: 'logged',

			type: 'PRIVATE',
			needContext: null,
			allowedRestrictions: ['SIMULATED', '2FA_EXPIRED'],
		},
	],
	orderImport: orderImportRoutes,
};

export default pageGenerator(pages);
